import React from "react";
import { toast } from "sonner";
import { X, CheckCircle2, Send, Loader2, Building2 } from "lucide-react";
import { api } from "../lib/api";

const INDUSTRIES = [
  { v: "webshop", l: "Webshop / E-commerce" },
  { v: "advokat", l: "Advokat / Revisor" },
  { v: "klinik", l: "Klinik / Apotek" },
  { v: "restaurant", l: "Restaurant / Catering" },
  { v: "kontor", l: "Kontor / Bureau" },
  { v: "andet", l: "Andet" },
];

const VOLUMES = [
  { v: "<10", l: "Under 10 / md" },
  { v: "10-30", l: "10 – 30 / md" },
  { v: "30-75", l: "30 – 75 / md" },
  { v: "75+", l: "75+ / md" },
  { v: "unknown", l: "Ved ikke endnu" },
];

const TIERS = [
  { v: "starter", l: "Starter (1.999 kr/md)" },
  { v: "business", l: "Business (4.499 kr/md)" },
  { v: "premium", l: "Premium (8.999 kr/md)" },
  { v: "custom", l: "Skræddersyet aftale" },
];

const empty = {
  company_name: "",
  cvr: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  industry: "",
  expected_volume: "",
  tier_interest: "",
  message: "",
};

export default function BusinessLeadForm({ open, onClose, initialTier = "" }) {
  const [form, setForm] = React.useState({ ...empty, tier_interest: initialTier });
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setForm({ ...empty, tier_interest: initialTier });
      setDone(false);
    }
  }, [open, initialTier]);

  React.useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.contact_email || !form.contact_phone) {
      toast.error("Udfyld venligst firma, kontaktperson, email og telefon.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/business/leads", {
        ...form,
        source: "erhverv_page",
      });
      setDone(true);
      toast.success("Tak! Vi vender tilbage inden for én arbejdsdag.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Noget gik galt — prøv igen eller skriv direkte til kontakt@emakurer.dk";
      toast.error(typeof msg === "string" ? msg : "Der opstod en fejl");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6" data-testid="business-lead-modal">
      <div className="absolute inset-0 bg-brand-dark/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-brand max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-brand-dark text-white px-6 py-5 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-brand-red flex items-center justify-center"><Building2 size={18} /></div>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/60">For erhverv</div>
              <div className="font-display font-semibold text-lg">Få et tilbud</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 transition-colors" data-testid="lead-form-close" aria-label="Luk">
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="px-6 py-12 text-center">
            <div className="w-14 h-14 bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
              <CheckCircle2 size={28} />
            </div>
            <h3 className="font-display font-semibold text-2xl text-brand-dark mt-5">Tak, {form.contact_name.split(" ")[0]}!</h3>
            <p className="text-brand-text2 mt-3 max-w-md mx-auto leading-relaxed">
              Vi har modtaget din forespørgsel og vender tilbage til <strong className="text-brand-dark">{form.contact_email}</strong> inden for én arbejdsdag med et skræddersyet tilbud.
            </p>
            <p className="text-brand-text2/70 text-xs mt-6">Haster det? Ring eller skriv direkte til <a href="mailto:kontakt@emakurer.dk" className="text-brand-red font-semibold">kontakt@emakurer.dk</a></p>
            <button onClick={onClose} className="btn-secondary mt-8" data-testid="lead-form-ok">Luk</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-5" data-testid="business-lead-form">
            <p className="text-brand-text2 text-sm leading-relaxed">
              Udfyld kort om jeres virksomhed, så vender vores erhvervsteam tilbage inden for én arbejdsdag med et tilbud tilpasset jeres behov.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-flat">Firmanavn *</label>
                <input className="input-flat" value={form.company_name} onChange={update("company_name")} data-testid="lead-company" required autoFocus />
              </div>
              <div>
                <label className="label-flat">CVR</label>
                <input className="input-flat" value={form.cvr} onChange={update("cvr")} data-testid="lead-cvr" placeholder="8 cifre" maxLength={10} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-flat">Kontaktperson *</label>
                <input className="input-flat" value={form.contact_name} onChange={update("contact_name")} data-testid="lead-name" required />
              </div>
              <div>
                <label className="label-flat">Telefon *</label>
                <input type="tel" className="input-flat" value={form.contact_phone} onChange={update("contact_phone")} data-testid="lead-phone" required />
              </div>
            </div>

            <div>
              <label className="label-flat">Email *</label>
              <input type="email" className="input-flat" value={form.contact_email} onChange={update("contact_email")} data-testid="lead-email" required />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-flat">Branche</label>
                <select className="input-flat" value={form.industry} onChange={update("industry")} data-testid="lead-industry">
                  <option value="">— Vælg —</option>
                  {INDUSTRIES.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}
                </select>
              </div>
              <div>
                <label className="label-flat">Forventet volumen</label>
                <select className="input-flat" value={form.expected_volume} onChange={update("expected_volume")} data-testid="lead-volume">
                  <option value="">— Vælg —</option>
                  {VOLUMES.map((v) => <option key={v.v} value={v.v}>{v.l}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="label-flat">Pakke-interesse</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {TIERS.map((t) => (
                  <button
                    type="button"
                    key={t.v}
                    onClick={() => setForm((f) => ({ ...f, tier_interest: t.v }))}
                    className={`text-xs font-semibold tracking-wider uppercase px-3 py-2.5 border transition-colors ${
                      form.tier_interest === t.v
                        ? "bg-brand-dark text-white border-brand-dark"
                        : "bg-white text-brand-text2 border-brand hover:border-brand-dark"
                    }`}
                    data-testid={`lead-tier-${t.v}`}
                  >
                    {t.l.split(" ")[0]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label-flat">Besked (valgfri)</label>
              <textarea
                className="input-flat min-h-[90px]"
                value={form.message}
                onChange={update("message")}
                data-testid="lead-message"
                placeholder="Fortæl kort om jeres behov — hentested, modtagere, særlige ønsker..."
                rows={3}
              />
            </div>

            <div className="bg-brand-bg2 border-l-2 border-brand-red px-4 py-3 text-xs text-brand-text2 leading-relaxed">
              Ved at indsende formularen accepterer du, at vi gemmer dine oplysninger med henblik på opfølgning. Vi sender ingen marketing-mails. Se vores <a href="/handelsbetingelser#privacy" className="text-brand-red font-semibold underline">privatlivspolitik</a>.
            </div>

            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="lead-form-submit">
              {submitting ? (<><Loader2 size={16} className="animate-spin" /> Sender...</>) : (<>Send forespørgsel <Send size={16} /></>)}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
